import type { Lead } from '../../../../shared/types';
import { updateLead } from '../../data/store';
import type { LeadPatch } from '../../data/store';
import { todayLocal, formatDate } from '../../logic/dates';
import { input, section } from '../../ui/components';
import { h, span } from '../../ui/dom';
import { money } from '../../ui/labels';

/** GPV remains editable; legacy milestone dates are readable history, not a second stage row. */

const MILESTONES: { field: keyof Pick<Lead, 'meeting_on' | 'demo_on' | 'application_on' | 'activation_on'>; label: string }[] = [
  { field: 'meeting_on', label: 'Meeting' },
  { field: 'demo_on', label: 'Demo' },
  { field: 'application_on', label: 'Application' },
  { field: 'activation_on', label: 'Activation' },
];

export const dealCard = (lead: Lead, onError: (err: unknown) => void): HTMLElement => {
  const save = (patch: LeadPatch) => void updateLead(lead.id, patch).catch(onError);

  /** Saved when the field is committed (a date picked, a number left), never on each keystroke:
   *  a write re-renders the lead page, and a field saving mid-typing would lose the caret. The
   *  value last written is remembered here, so a blur after a change does not write it twice. */
  const onCommit = (el: HTMLInputElement, written: string, fn: (value: string) => void) => {
    let last = written;
    const commit = () => {
      if (el.value === last) return;
      last = el.value;
      fn(el.value);
    };
    el.addEventListener('change', commit);
    el.addEventListener('blur', commit);
  };

  const gpvInput = input({
    type: 'number',
    inputmode: 'decimal',
    min: '0',
    step: '100',
    value: lead.gpv_usd === null ? '' : String(lead.gpv_usd),
    class: 'input--sm',
    ariaLabel: 'Monthly card volume in dollars',
  });
  onCommit(gpvInput, gpvInput.value, (v) => {
    const next = v === '' ? null : Number(v);
    if (next !== null && Number.isNaN(next)) return;
    save({ gpv_usd: next, gpv_as_of: next === null ? null : todayLocal() });
  });

  return section(
    { title: 'Deal' },
    h(
      'div',
      { class: 'card' },
      h(
        'div',
        { class: 'dealrow' },
        span('dealrow__label', 'Monthly GPV'),
        gpvInput,
      ),
      lead.gpv_usd !== null
        ? h(
            'p',
            { class: 'muted small' },
            `${money(lead.gpv_usd)} a month, as you reported it${lead.gpv_as_of ? ` on ${lead.gpv_as_of}` : ''}.`,
          )
        : h('p', { class: 'muted small' }, 'Your own figure for the merchant, not read from anywhere.'),
      h('details', {},
        h('summary', {}, 'Legacy milestone dates · read only'),
        h('p', { class: 'muted small' }, 'Saved dates from the retired Conversion view. They do not determine lead stage or Reports counts.'),
        MILESTONES.map(({ field, label }) => h('p', { class: 'muted small' }, `${label}: ${lead[field] ? formatDate(lead[field]) : 'Not recorded'}`)),
      ),
    ),
  );
};
